import { useRef, useState } from 'react';
import { Button, DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import type { StatisticsDateRange } from '../types/date-range';

interface Props {
  value: StatisticsDateRange;
  onChange: (range: StatisticsDateRange) => void;
  showApply?: boolean;
  subject?: 'profile' | 'access';
}

const reportingDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Ho_Chi_Minh',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});
const isFutureDate = (date: Dayjs) =>
  date.format('YYYY-MM-DD') > reportingDateFormatter.format(new Date());

export function StatisticsDateRangePicker({
  value,
  onChange,
  showApply = false,
  subject = 'access'
}: Props) {
  const [open, setOpen] = useState(false);
  const groupRef = useRef<HTMLDivElement>(null);
  const opened = useRef(false);
  const draft = useRef<[Dayjs | null, Dayjs | null] | null>(null);
  const clearDraftDate = (input: HTMLInputElement) => {
    if (input.value || !groupRef.current) return false;
    const inputs = groupRef.current.querySelectorAll('input');
    const index = input === inputs[0] ? 0 : input === inputs[1] ? 1 : null;
    if (index === null) return false;
    const next: [Dayjs | null, Dayjs | null] = draft.current
      ? [...draft.current]
      : [null, null];
    next[index] = null;
    draft.current = next;
    return true;
  };
  const commit = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates?.some((date) => date && isFutureDate(date))) return;
    onChange([
      dates?.[0]?.format('YYYY-MM-DD') ?? null,
      dates?.[1]?.format('YYYY-MM-DD') ?? null
    ]);
  };
  return (
    <div
      ref={groupRef}
      role='group'
      onInputCapture={(event) => {
        if (event.target instanceof HTMLInputElement)
          clearDraftDate(event.target);
      }}
      aria-label={
        subject === 'profile'
          ? 'Khoảng ngày tạo hồ sơ'
          : 'Khoảng thời điểm truy cập'
      }
    >
      <DatePicker.RangePicker
        format='DD/MM/YYYY'
        disabledDate={isFutureDate}
        open={showApply ? open : undefined}
        onCalendarChange={(dates, _dateStrings, info) => {
          if (!opened.current) return;
          const next: [Dayjs | null, Dayjs | null] = draft.current
            ? [...draft.current]
            : [null, null];
          const index = info.range === 'start' ? 0 : 1;
          next[index] = dates[index];
          draft.current = next;
        }}
        onOpenChange={(next) => {
          if (next)
            draft.current = [
              value[0] ? dayjs(value[0]) : null,
              value[1] ? dayjs(value[1]) : null
            ];
          else if (showApply && opened.current) commit(draft.current);
          opened.current = next;
          if (showApply) setOpen(next);
        }}
        onBlur={(event) => {
          // RangePicker does not emit onChange when a typed date becomes empty.
          if (
            event.target instanceof HTMLInputElement &&
            clearDraftDate(event.target) &&
            !showApply
          )
            commit(draft.current);
        }}
        value={[
          value[0] ? dayjs(value[0]) : null,
          value[1] ? dayjs(value[1]) : null
        ]}
        order={false}
        allowEmpty={[true, true]}
        onChange={(dates) => {
          if (!dates) draft.current = null;
          if (!showApply || !dates) commit(dates);
        }}
        placeholder={['Từ ngày', 'Đến ngày']}
        renderExtraFooter={
          showApply
            ? () => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: '6px 12px'
                  }}
                >
                  <Button
                    type='primary'
                    size='small'
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      commit(draft.current);
                      opened.current = false;
                      setOpen(false);
                    }}
                  >
                    Áp dụng
                  </Button>
                </div>
              )
            : undefined
        }
      />
    </div>
  );
}
